import React, { useEffect, useMemo, useState } from 'react';
import { AppBar, Toolbar, Container, Box, Button, IconButton, Drawer, List, ListItem, ListItemButton, Divider, Stack, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../../img/nekrodruk-logo-dark.png';
import { useAuth } from '../../../context/AuthContext';

const primaryButtonStyles = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    borderRadius: 2,
    px: 3,
    py: 1,
    backgroundColor: '#5189B0',
    boxShadow: '0 10px 30px rgba(81, 137, 176, 0.25)',
    '&:hover': {
        backgroundColor: '#63b3be',
        boxShadow: '0 12px 30px rgba(99, 179, 190, 0.35)',
    },
};

const textButtonStyles = {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '1rem',
    color: '#1D1D1F',
    '&:hover': {
        color: '#5189B0',
    },
    '&.active': {
        color: '#5189B0',
    },
};

export default function NavLogged() {
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { setIsLoggedIn, isOwner } = useAuth();

    const menuItems = useMemo(() => (
        [
            { label: 'Moje konto', to: isOwner ? '/account' : '/accountemployee' },
            { label: 'Generowanie', to: '/personForm' },
            { label: 'Szarfy', to: '/sash' },
            { label: 'Subskrypcja', to: '/subscription' },
        ]
    ), [isOwner]);

    const closeMenu = () => setMenuOpen(false);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    const logoutHandler = () => {
        setIsLoggedIn(false);
        localStorage.removeItem('token');
        navigate('/');
    };

    const renderMenuButtons = (onClick) => (
        menuItems.map(({ label, to }) => (
            <Button
                key={to}
                component={NavLink}
                to={to}
                onClick={onClick}
                sx={textButtonStyles}
            >
                {label}
            </Button>
        ))
    );

    const handleDrawerNavigate = (to) => {
        closeMenu();
        navigate(to);
    };

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(227,247,249,0.95) 100%)',
                    backdropFilter: 'blur(12px)',
                    borderBottom: '1px solid rgba(81, 137, 176, 0.1)',
                    color: '#1D1D1F',
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: { xs: 72, md: 88 } }}>
                        <Box component={NavLink} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <Box
                                component="img"
                                src={logo}
                                alt="logo nekrodruk"
                                sx={{ height: { xs: 36, md: 48 } }}
                            />
                        </Box>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
                            {renderMenuButtons()}
                            <Button
                                onClick={logoutHandler}
                                variant="contained"
                                sx={primaryButtonStyles}
                            >
                                Wyloguj się
                            </Button>
                        </Stack>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => setMenuOpen(true)}
                            sx={{ display: { xs: 'flex', md: 'none' } }}
                            aria-label="Otwórz menu"
                        >
                            <MenuIcon />
                        </IconButton>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="right"
                open={menuOpen}
                onClose={closeMenu}
                PaperProps={{
                    sx: {
                        width: { xs: 'min(86vw, 320px)', sm: 320 },
                        backgroundColor: '#FFFFFF',
                        backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(227,247,249,0.95) 100%)',
                        padding: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    },
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: 2 }}>
                        MENU
                    </Typography>
                    <IconButton onClick={closeMenu} aria-label="Zamknij menu">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ borderColor: 'rgba(81, 137, 176, 0.2)' }} />
                <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {menuItems.map(({ label, to }) => (
                        <ListItem key={to} disablePadding>
                            <ListItemButton
                                selected={location.pathname === to}
                                onClick={() => handleDrawerNavigate(to)}
                                sx={{
                                    borderRadius: 2,
                                    color: '#1D1D1F',
                                    textAlign: 'center',
                                    '&.Mui-selected': {
                                        color: '#5189B0',
                                        backgroundColor: 'rgba(81, 137, 176, 0.08)',
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: 'rgba(81, 137, 176, 0.12)',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'rgba(81, 137, 176, 0.1)',
                                        color: '#5189B0',
                                    },
                                }}
                            >
                                {label}
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
                <Divider sx={{ borderColor: 'rgba(81, 137, 176, 0.2)' }} />
                <Button
                    variant="contained"
                    onClick={() => {
                        closeMenu();
                        logoutHandler();
                    }}
                    sx={{
                        ...primaryButtonStyles,
                        width: '100%',
                        mt: 1,
                    }}
                >
                    Wyloguj się
                </Button>
            </Drawer>
        </>
    );
}
